import { generateUUID } from '../utils/uuid';
/**
 * useCodingSuggestions — fetches and manages coding suggestion state.
 *
 * Handles: loading, accepting, rejecting, local optimistic updates.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSession } from '../context/SessionContext';
import * as api from '../services/api';
import { mockWrite } from '../services/fhirWriteback';
import type {
  CodingSuggestionsResponse,
  CodingSuggestion,
  GapSummary,
  FilterState,
  SortField,
  WorkflowMode,
  ConditionGroupData,
  ProblemListItem,
  ProviderSpecialty,
  EHRWriteBackEvent,
} from '../types';
import { isRelevantToSpecialty } from '../utils/specialtyMapping';

const SYNC_CHANNEL = 'raapid-suggestions-sync';
const EHR_SYNC_CHANNEL = 'raapid-ehr-sync';

function recomputeGapSummary(suggestions: CodingSuggestion[]): GapSummary {
  return {
    totalSuggestions: suggestions.length,
    suspectNew: suggestions.filter(s => s.gapType === 'suspect_new').length,
    opportunity: suggestions.filter(s => s.gapType === 'opportunity').length,
    recapture: suggestions.filter(s => s.gapType === 'recapture').length,
    staleRecapture: suggestions.filter(s => s.gapType === 'recapture_stale').length,
    overClaimed: suggestions.filter(s => s.gapType === 'over_claimed').length,
    pendingReview: suggestions.filter(s => s.status === 'pending').length,
    accepted: suggestions.filter(s => s.status === 'accepted').length,
    rejected: suggestions.filter(s => s.status === 'rejected').length,
    hccGaps: suggestions.filter(s => s.hccCategory != null).length,
  };
}

// Red → Blue → Green → Amber → Purple (clinical urgency order)
const GAP_TYPE_PRIORITY: Record<string, number> = {
  over_claimed: 1,
  suspect_new: 2,
  opportunity: 3,
  recapture_stale: 4,
  recapture: 5,
};

function defaultFiltersForMode(_mode: WorkflowMode): FilterState {
  return {
    status: 'all',
    gapType: 'all',
    specialty: 'all',
    hccOnly: false,
    mySpecialtyOnly: false,
    sortBy: 'clinicalPriority',
    sortDir: 'asc',
    searchQuery: '',
  };
}

export function useCodingSuggestions() {
  const { launch, workflowMode, isLoading: sessionLoading } = useSession();
  const [data, setData] = useState<CodingSuggestionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(() => defaultFiltersForMode(workflowMode));
  /** Per-suggestion loading state for accept/reject buttons */
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [attestation, setAttestation] = useState<{ attestedAt: string; attestedBy: string } | null>(null);
  const suppressSyncRef = useRef(false);

  // ── Cross-iframe sync via BroadcastChannel ────────────────────────────────
  const channelRef = useRef<BroadcastChannel | null>(null);
  const ehrChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const ch = new BroadcastChannel(SYNC_CHANNEL);
    const ehrCh = new BroadcastChannel(EHR_SYNC_CHANNEL);
    channelRef.current = ch;
    ehrChannelRef.current = ehrCh;
    ch.onmessage = (e) => {
      const msg = e.data;
      if (msg?.patientId !== launch.raapidPatientId) return;
      suppressSyncRef.current = true;
      if (msg.type === 'attestation') {
        setAttestation(msg.attestation);
      } else {
        setData((prev) => {
          if (!prev) return prev;
          const suggestions = msg.suggestions as CodingSuggestion[];
          return { ...prev, suggestions, gapSummary: recomputeGapSummary(suggestions) };
        });
      }
      setTimeout(() => { suppressSyncRef.current = false; }, 50);
    };
    ehrCh.onmessage = (e) => {
      const msg = e.data as EHRWriteBackEvent;
      if (msg?.patientId !== launch.raapidPatientId) return;
      if (msg.type === 'code-undone' && msg.code) {
        setData((prev) => {
          if (!prev) return prev;
          const suggestions = prev.suggestions.map((s) =>
            s.icdCode === msg.code!.icd && s.status !== 'pending'
              ? { ...s, status: 'pending' as const, acceptedAt: undefined, acceptedBy: undefined, rejectedAt: undefined, rejectedBy: undefined, rejectionReason: undefined }
              : s,
          );
          broadcastSync(suggestions);
          return { ...prev, suggestions, gapSummary: recomputeGapSummary(suggestions) };
        });
      }
      if (msg.type === 'code-accepted' && msg.code) {
        setData((prev) => {
          if (!prev) return prev;
          const suggestions = prev.suggestions.map((s) =>
            s.icdCode === msg.code!.icd && s.status === 'pending'
              ? { ...s, status: 'accepted' as const, acceptedAt: new Date().toISOString(), acceptedBy: msg.code!.providerName || 'Provider' }
              : s,
          );
          broadcastSync(suggestions);
          return { ...prev, suggestions, gapSummary: recomputeGapSummary(suggestions) };
        });
      }
    };
    return () => { ch.close(); ehrCh.close(); };
  }, [launch.raapidPatientId]);

  const broadcastEHR = useCallback((event: EHRWriteBackEvent) => {
    ehrChannelRef.current?.postMessage(event);
  }, []);

  const broadcastSync = useCallback((suggestions: CodingSuggestion[]) => {
    if (suppressSyncRef.current) return;
    channelRef.current?.postMessage({ patientId: launch.raapidPatientId, suggestions });
  }, [launch.raapidPatientId]);

  // Reset sort defaults when workflow mode changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      sortBy: 'clinicalPriority',
    }));
  }, [workflowMode]);

  // ── Fetch data ─────────────────────────────────────────────────────────────

  useEffect(() => {
    // In SMART mode, wait until useSMARTLaunch has resolved the real patient ID.
    if (sessionLoading) return;
    if (!launch.raapidPatientId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const accessToken =
      launch.smartClient != null
        ? (launch.smartClient as { state: { tokenResponse?: { access_token?: string } } }).state?.tokenResponse?.access_token
        : undefined;

    api
      .getCodingSuggestions(launch.raapidPatientId, accessToken)
      .then((res) => {
        if (!cancelled) {
          setData(res);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load suggestions.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [launch.raapidPatientId, launch.smartClient, sessionLoading]);

  // ── Accept ─────────────────────────────────────────────────────────────────

  const acceptSuggestion = useCallback(
    async (suggestionId: string) => {
      if (!data || !launch.raapidPatientId) return;

      setActionLoading((prev) => ({ ...prev, [suggestionId]: true }));

      try {
        // In mock/extension mode, simulate FHIR write
        const suggestion = data.suggestions.find((s) => s.id === suggestionId);
        if (!suggestion) throw new Error('Suggestion not found');

        let fhirResourceId: string;
        let fhirResponseStatus: number;

        if (launch.mode === 'mock' || launch.mode === 'extension') {
          const result = await mockWrite(suggestion);
          fhirResourceId = result.fhirResourceId;
          fhirResponseStatus = result.fhirResponseStatus;
        } else {
          const result = await api.acceptSuggestion(
            launch.raapidPatientId,
            {
              suggestionId,
              acceptedBy: launch.providerName ?? 'Unknown Provider',
              encounterId: launch.encounterId ?? data.encounter.encounterId,
              writeBackMethod: 'DocumentReference',
            },
          );
          fhirResourceId = result.writeBack?.fhirResourceId ?? '';
          fhirResponseStatus = result.writeBack?.fhirResponseStatus ?? 201;
        }

        // Optimistic update
        const now = new Date().toISOString();
        setData((prev) => {
          if (!prev) return prev;
          const suggestions = prev.suggestions.map((s) =>
            s.id === suggestionId
              ? {
                  ...s,
                  status: 'accepted' as const,
                  acceptedAt: now,
                  acceptedBy: launch.providerName ?? 'Provider',
                  writeBack: {
                    method: 'DocumentReference' as const,
                    fhirResourceId,
                    fhirResponseStatus,
                  },
                }
              : s,
          );
          broadcastSync(suggestions);
          return { ...prev, suggestions, gapSummary: recomputeGapSummary(suggestions) };
        });

        broadcastEHR({
          type: 'code-accepted',
          patientId: launch.raapidPatientId,
          code: {
            icd: suggestion.icdCode,
            description: suggestion.icdDescription,
            hcc: suggestion.hccCategory,
            gapType: suggestion.gapType,
            evidence: suggestion.evidence[0]?.sourceDocument ?? '',
            providerName: launch.providerName ?? 'Provider',
            timestamp: now,
            suggestionId,
          },
        });
      } catch (err) {
        throw err; // Re-throw so AcceptButton can show error state
      } finally {
        setActionLoading((prev) => ({ ...prev, [suggestionId]: false }));
      }
    },
    [data, launch, broadcastSync, broadcastEHR],
  );

  // ── Reject ─────────────────────────────────────────────────────────────────

  const rejectSuggestion = useCallback(
    async (suggestionId: string, reason: string) => {
      if (!data || !launch.raapidPatientId) return;

      setActionLoading((prev) => ({ ...prev, [suggestionId]: true }));

      try {
        if (launch.mode !== 'mock' && launch.mode !== 'extension') {
          await api.rejectSuggestion(launch.raapidPatientId, {
            suggestionId,
            rejectedBy: launch.providerName ?? 'Unknown Provider',
            rejectionReason: reason,
          });
        } else {
          await new Promise((r) => setTimeout(r, 400));
        }

        setData((prev) => {
          if (!prev) return prev;
          const now = new Date().toISOString();
          const suggestions = prev.suggestions.map((s) =>
            s.id === suggestionId
              ? {
                  ...s,
                  status: 'rejected' as const,
                  rejectedAt: now,
                  rejectedBy: launch.providerName ?? 'Provider',
                  rejectionReason: reason,
                }
              : s,
          );
          broadcastSync(suggestions);
          return { ...prev, suggestions, gapSummary: recomputeGapSummary(suggestions) };
        });

        const suggestion = data.suggestions.find((s) => s.id === suggestionId);
        if (suggestion) {
          broadcastEHR({
            type: 'code-rejected',
            patientId: launch.raapidPatientId,
            code: {
              icd: suggestion.icdCode,
              description: suggestion.icdDescription,
              hcc: suggestion.hccCategory,
              gapType: suggestion.gapType,
              evidence: suggestion.evidence[0]?.sourceDocument ?? '',
              providerName: launch.providerName ?? 'Provider',
              timestamp: new Date().toISOString(),
              suggestionId,
            },
            rejectionReason: reason,
          });
        }
      } finally {
        setActionLoading((prev) => ({ ...prev, [suggestionId]: false }));
      }
    },
    [data, launch, broadcastSync, broadcastEHR],
  );

  // ── Edit suggestion ────────────────────────────────────────────────────────

  const editSuggestion = useCallback(
    (suggestionId: string, updates: Partial<CodingSuggestion>) => {
      setData((prev) => {
        if (!prev) return prev;
        const suggestions = prev.suggestions.map((s) =>
          s.id === suggestionId ? { ...s, ...updates } : s,
        );
        broadcastSync(suggestions);
        return { ...prev, suggestions, gapSummary: recomputeGapSummary(suggestions) };
      });
    },
    [broadcastSync],
  );

  // ── Add new suggestion ────────────────────────────────────────────────────

  const addSuggestion = useCallback(
    (newSuggestion: Omit<CodingSuggestion, 'id'>) => {
      const id = generateUUID();
      setData((prev) => {
        if (!prev) return prev;
        const suggestions = [...prev.suggestions, { ...newSuggestion, id }];
        broadcastSync(suggestions);
        return { ...prev, suggestions, gapSummary: recomputeGapSummary(suggestions) };
      });
    },
    [broadcastSync],
  );

  // ── Reset status to pending ───────────────────────────────────────────────

  const resetSuggestionStatus = useCallback(
    (suggestionId: string) => {
      if (!data || !launch.raapidPatientId) return;
      const suggestion = data.suggestions.find((s) => s.id === suggestionId);
      setData((prev) => {
        if (!prev) return prev;
        const suggestions = prev.suggestions.map((s) =>
          s.id === suggestionId
            ? {
                ...s,
                status: 'pending' as const,
                acceptedAt: undefined,
                acceptedBy: undefined,
                writeBack: undefined,
                rejectedAt: undefined,
                rejectedBy: undefined,
                rejectionReason: undefined,
              }
            : s,
        );
        broadcastSync(suggestions);
        return { ...prev, suggestions, gapSummary: recomputeGapSummary(suggestions) };
      });
      if (suggestion) {
        broadcastEHR({
          type: 'code-undone',
          patientId: launch.raapidPatientId,
          code: {
            icd: suggestion.icdCode,
            description: suggestion.icdDescription,
            hcc: suggestion.hccCategory,
            gapType: suggestion.gapType,
            evidence: '',
            providerName: '',
            timestamp: '',
            suggestionId,
          },
        });
      }
    },
    [data, launch, broadcastSync, broadcastEHR],
  );

  // ── Attestation ────────────────────────────────────────────────────────────

  const attestSession = useCallback(
    (providerName: string) => {
      const att = { attestedAt: new Date().toISOString(), attestedBy: providerName };
      setAttestation(att);
      if (!suppressSyncRef.current) {
        channelRef.current?.postMessage({ patientId: launch.raapidPatientId, type: 'attestation', attestation: att });
      }
      const acceptedCodes = (data?.suggestions ?? [])
        .filter((s) => s.status === 'accepted')
        .map((s) => ({ icd: s.icdCode, description: s.icdDescription, action: 'accepted' }));
      const rejectedCodes = (data?.suggestions ?? [])
        .filter((s) => s.status === 'rejected')
        .map((s) => ({ icd: s.icdCode, description: s.icdDescription, action: 'deferred' }));
      broadcastEHR({
        type: 'attested',
        patientId: launch.raapidPatientId ?? '',
        attestation: {
          by: providerName,
          at: att.attestedAt,
          specialty: launch.providerSpecialty,
          codes: [...acceptedCodes, ...rejectedCodes],
        },
      });
    },
    [launch.raapidPatientId, launch.providerSpecialty, data, broadcastEHR],
  );

  // ── Filter + Sort (memoized — only recomputes when data or filters change) ──

  const filteredSuggestions = useMemo(() => {
    return (data?.suggestions ?? [])
      .filter((s) => {
        if (filters.status !== 'all' && s.status !== filters.status) return false;
        if (filters.gapType !== 'all' && s.gapType !== filters.gapType) return false;
        if (filters.hccOnly && !s.hccCategory) return false;
        if (filters.specialty !== 'all' && s.hccCategory && !isRelevantToSpecialty(s.hccCategory, filters.specialty as ProviderSpecialty)) return false;
        if (filters.mySpecialtyOnly && launch.providerSpecialty && s.hccCategory && !isRelevantToSpecialty(s.hccCategory, launch.providerSpecialty)) return false;
        if (filters.searchQuery) {
          const q = filters.searchQuery.toLowerCase();
          if (
            !s.icdCode.toLowerCase().includes(q) &&
            !s.icdDescription.toLowerCase().includes(q) &&
            !s.hccDescription?.toLowerCase().includes(q)
          )
            return false;
        }
        return true;
      })
      .sort((a, b) => {
        let cmp = 0;
        const field: SortField = filters.sortBy;
        if (field === 'clinicalPriority') {
          const cmpP = (GAP_TYPE_PRIORITY[a.gapType] ?? 99) - (GAP_TYPE_PRIORITY[b.gapType] ?? 99);
          return filters.sortDir === 'asc' ? cmpP : -cmpP;
        }
        if (field === 'rafImpact') cmp = b.hccWeight - a.hccWeight;
        else if (field === 'gapType') cmp = a.gapType.localeCompare(b.gapType);
        else if (field === 'icdCode') cmp = a.icdCode.localeCompare(b.icdCode);
        return filters.sortDir === 'asc' ? -cmp : cmp;
      });
  }, [data?.suggestions, filters, launch.providerSpecialty]);

  // ── Group by condition / HCC category ─────────────────────────────────────

  const conditionGroups = useMemo((): ConditionGroupData[] => {
    const suggestions = filteredSuggestions;
    const problemList: ProblemListItem[] = data?.problemList ?? [];

    const groupMap = new Map<string, ConditionGroupData>();

    for (const s of suggestions) {
      const key = s.hccCategory ?? `icd:${s.icdCode}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          hccCategory: s.hccCategory,
          hccDescription: s.hccDescription,
          hccWeight: s.hccWeight,
          problemListItems: [],
          suggestions: [],
        });
      }
      groupMap.get(key)!.suggestions.push(s);
    }

    for (const p of problemList) {
      const key = p.hccCategory ?? `icd:${p.icdCode}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          hccCategory: p.hccCategory,
          hccDescription: p.hccDescription,
          hccWeight: 0,
          problemListItems: [],
          suggestions: [],
        });
      }
      const group = groupMap.get(key)!;
      const alreadyAsSuggestion = group.suggestions.some(s => s.icdCode === p.icdCode);
      if (!alreadyAsSuggestion) {
        group.problemListItems.push(p);
      }
    }

    return Array.from(groupMap.values()).sort((a, b) => {
      if (filters.sortBy === 'clinicalPriority') {
        const aPri = Math.min(...(a.suggestions.length ? a.suggestions.map(s => GAP_TYPE_PRIORITY[s.gapType] ?? 99) : [99]));
        const bPri = Math.min(...(b.suggestions.length ? b.suggestions.map(s => GAP_TYPE_PRIORITY[s.gapType] ?? 99) : [99]));
        const cmp = aPri - bPri;
        return filters.sortDir === 'asc' ? cmp : -cmp;
      }
      if (filters.sortBy === 'rafImpact') {
        const cmp = b.hccWeight - a.hccWeight;
        return filters.sortDir === 'asc' ? -cmp : cmp;
      }
      if (filters.sortBy === 'icdCode') {
        const aKey = a.hccCategory ?? a.suggestions[0]?.icdCode ?? '';
        const bKey = b.hccCategory ?? b.suggestions[0]?.icdCode ?? '';
        const cmp = aKey.localeCompare(bKey);
        return filters.sortDir === 'asc' ? cmp : -cmp;
      }
      // gapType or fallback — sort by hccWeight
      const cmp = b.hccWeight - a.hccWeight;
      return filters.sortDir === 'asc' ? -cmp : cmp;
    });
  }, [filteredSuggestions, data?.problemList]);

  return {
    data,
    isLoading,
    error,
    filters,
    setFilters,
    filteredSuggestions,
    actionLoading,
    acceptSuggestion,
    rejectSuggestion,
    editSuggestion,
    addSuggestion,
    resetSuggestionStatus,
    attestation,
    attestSession,
    conditionGroups,
  };
}
